import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import FormDialog from './questionGridDialog';
import GenerateQuestiondialog from './GenerateQuestiondialog';
import DashboardNavbar from '../dashboardNavbar';
import DataTable from '../ui/DataTable';
import { StatusBadge, Btn } from '../ui/primitives';
import QuestionRowActionsMenu from './QuestionRowActionsMenu';
import PageActionsMenu from '../ui/PageActionsMenu';
import { Sparkles } from 'lucide-react';
import { Icon } from '../ui/shell';
import { LabelWithHelp } from '../ui/FieldHelp';
import { LIST_TABLE_FIELD_HELP as TABLE_HELP } from '../ui/settingsFieldHelp';
import { getCache, setCache, clearCacheByPrefix } from '../ui/listCache';
import { useConfirm } from '../ui/ConfirmProvider';
import {
  buildQuestionListQuery,
  buildQuestionListCacheKey,
  parseQuestionListResponse,
  QUESTION_LIST_DEFAULTS,
  QUESTION_SORT_FIELDS,
  getNextQuestionSort,
  sortQuestionsLocally,
} from './questionListApi';
import { useAppContext } from '../../appprovider';
import {
  canUseAiCourseBuilder,
  aiCourseBuilderGateBody,
} from '../services/instructorMeterLimits';
import useDemoActionGate from '../hooks/useDemoActionGate';
import DemoLockedDialog from '../ui/DemoLockedDialog';

// Force ESLint cache refresh
const QUESTION_PAGE_SIZE = 10;

// Friendly labels for the question_type field (Direct / Scenario / Passage / Image).
// Distinct from mimeType, which is no longer shown here but is still saved and
// relied on by other APIs.
const QUESTION_TYPE_LABELS = {
  Passage: 'Passage question',
  Scenario: 'Scenario question',
  Direct: 'Direct question',
  Image: 'Image question',
};
const initialValue = { courseId: '', studentId: '', courseName: '', firstName: '', lastName: '', studentEmail: '' };

const PageShell = styled.div`
  min-height: calc(100vh - 32px);
  margin: 0 auto;
  padding: 0 28px 36px;
  color: ${({ theme }) => theme.text};
  font-family: ${({ theme }) => theme.fontBody};

  @media (max-width: 768px) {
    padding: 0 14px 28px;
  }
`;

const BackBar = styled.div`
  width: min(1180px, 100%);
  margin: ${({ theme }) => theme.gap} auto 0;
`;

const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: 0 12px 0 8px;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: ${({ theme }) => theme.rMd};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.text};
  font-family: inherit;
  font-size: ${({ theme }) => theme.fsSm};
  font-weight: 650;
  cursor: pointer;

  &:hover { background: ${({ theme }) => theme.surface2}; }
`;

const PageHero = styled.div`
  width: min(1180px, 100%);
  margin: 10px auto ${({ theme }) => theme.gap};
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;

  h2 {
    margin: 0;
    color: ${({ theme }) => theme.text};
    font-family: ${({ theme }) => theme.fontHead};
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -.01em;
  }

  p {
    margin: 2px 0 0;
    color: ${({ theme }) => theme.textMuted};
    font-size: ${({ theme }) => theme.fsSm};
  }
`;

const TableWrap = styled.div`
  width: min(1180px, 100%);
  margin: 0 auto;
`;

const EmptyState = styled.div`
  width: min(1180px, 100%);
  margin: ${({ theme }) => theme.gap} auto 0;
  padding: 28px 24px;
  border-radius: ${({ theme }) => theme.rLg};
  border: ${({ theme }) => theme.cardBorder};
  background: ${({ theme }) => theme.surface};
  box-shadow: ${({ theme }) => theme.cardShadow};

  p {
    margin: 0 0 14px;
    color: ${({ theme }) => theme.textMuted};
    font-size: ${({ theme }) => theme.fsSm};
  }
`;

const QuestionsPage = () => {
  const askConfirmation = useConfirm();
  const { guardAction, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
  const location = useLocation();
  const navigate = useNavigate();
  const { entitlements, openUpgradeGate } = useAppContext();
  const aiBuilderEnabled = canUseAiCourseBuilder(entitlements);
  const { testId, fromLabel } = location.state || {};
  const [loading, setLoading] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL;
  const [tableData, setTableData] = useState(null);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(initialValue);
  const [rowCount, setRowCount] = useState(0);
  const [questionPage, setQuestionPage] = useState(0);
  const [questionTotalPages, setQuestionTotalPages] = useState(1);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [debouncedKeyword, setDebouncedKeyword] = useState('');
  const [questionSortBy, setQuestionSortBy] = useState(QUESTION_LIST_DEFAULTS.sortBy);
  const [questionSortDir, setQuestionSortDir] = useState(QUESTION_LIST_DEFAULTS.sortDir);
  const [generateOpen, setGenerateOpen] = useState(false);
  const searchDebounceRef = useRef(null);

  const menuItems = [
    { title: 'Assessments', link: '/Portal/Instructor/Assignment' },
    { title: 'Questions', link: '/Portal/Instructor/Questions' },
  ];

  const getBasicAuthHeader = () => {
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;
    const encodedCredentials = btoa(`${username}:${password}`);
    return `Basic ${encodedCredentials}`;
  };

  const invalidateQuestionListCache = () => {
    if (testId) clearCacheByPrefix(`questions:${testId}:`);
  };

  const applyQuestionListPage = (parsed) => {
    setTableData(parsed.content);
    setRowCount(parsed.totalElements);
    setQuestionTotalPages(Math.max(parsed.totalPages, 1));
    setQuestionPage(parsed.page);
  };

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedKeyword(searchKeyword);
      setQuestionPage(0);
    }, 300);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchKeyword]);

  useEffect(() => {
    if (testId) getUsers(questionPage, debouncedKeyword);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testId, questionPage, debouncedKeyword]);

  const sortedTableData = useMemo(
    () => sortQuestionsLocally(tableData || [], questionSortBy, questionSortDir),
    [tableData, questionSortBy, questionSortDir],
  );

  const getUsers = async (page = 0, keyword = '') => {
    if (!testId) return;

    const listParams = { testId, page, size: QUESTION_PAGE_SIZE, keyword };
    const cacheKey = buildQuestionListCacheKey(testId, listParams);
    const cached = getCache(cacheKey);
    if (cached) {
      applyQuestionListPage(cached);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const query = buildQuestionListQuery(listParams);
      const response = await axios.get(`${apiUrl}/api/by-test?${query}`, {
        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
          'Access-Control-Allow-Origin': '*',
          Authorization: getBasicAuthHeader(),
        },
      });
      const parsed = parseQuestionListResponse(response.data);
      applyQuestionListPage(parsed);
      setCache(cacheKey, parsed);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuestionPageChange = (nextPage) => {
    const zeroBasedPage = Math.max(nextPage - 1, 0);
    setQuestionPage(zeroBasedPage);
  };

  const handleQuestionSortChange = (columnKey) => {
    const next = getNextQuestionSort(columnKey, questionSortBy, questionSortDir);
    setQuestionSortBy(next.sortBy);
    setQuestionSortDir(next.sortDir);
  };

  const handleFormSubmit = async () => {
    if (!formData) return;
    // Submit is an explicit action in the edit dialog, so we don't gate it behind a
    // typed-confirm dialog — that dialog opens outside the MUI Dialog's focus trap,
    // so its input can't be typed into, which left the update stuck and never called
    // the API.
    setLoading(true);
    try {
      const transformedFormData = {
        questionId: formData.questionId,
        test_id: formData.testId,
        domain_id: formData.domain_id,
        question: formData.question,
        explanation: formData.explanation,
        hint: formData.hint,
        keywords: formData.keywords,
        mimeType: formData.mimeType,
        asset: formData.asset || null,
        passage: formData.passage,
        caseStudyId: formData.caseStudyId,
        created_at: formData.createdAt,
        updated_at: formData.updatedAt,
        is_deleted: formData.isDeleted || 0,
        active: formData.active,
        correctAnswer: formData.correctAnswer,
        answer: (formData.answer || []).map((ans) => ({
          answerId: ans.answerId,
          answer: ans.text,
          is_correct: ans.isCorrect ? 1 : 0,
        })),
      };
      await axios.put(
        `${apiUrl}/api/questions/update-question`,
        transformedFormData,
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: '*/*',
            'Access-Control-Allow-Origin': '*',
            Authorization: getBasicAuthHeader(),
          },
        },
      );
      handleClose();
      invalidateQuestionListCache();
      getUsers(questionPage, debouncedKeyword);
    } catch (error) {
      console.error('Error updating question:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFormData(initialValue);
  };

  const onChange = (e, index = null) => {
    const { id, value } = e.target;
    if (index !== null) {
      const updatedAnswers = formData.answers.map((answer, idx) =>
        idx === index ? { ...answer, answer: value } : answer,
      );
      setFormData({ ...formData, answers: updatedAnswers });
    } else {
      setFormData({ ...formData, [id]: value });
    }
  };

  const handleUpdate = (oldData) => {
    setFormData(oldData);
    handleClickOpen();
  };

  const updateQuestionActiveStatus = async (row, newStatus) => {
    await axios.put(
      `${apiUrl}/api/update-active-status`,
      null,
      {
        params: {
          questionId: row.questionId,
          active: newStatus,
        },
        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
          'Access-Control-Allow-Origin': '*',
          Authorization: getBasicAuthHeader(),
        },
      },
    );
  };

  const handleActive = async (row) => {
    if (!guardAction('action.assessment.question.disable')) return;
    if (!row) return;
    const newStatus = row.active === 1 ? 0 : 1;
    const action = newStatus === 1 ? 'enable' : 'disable';
    const actionLabel = action.charAt(0).toUpperCase() + action.slice(1);
    const confirmed = await askConfirmation({
      title: `${actionLabel} question?`,
      message: `Are you sure you want to ${action} this question?`,
      confirmLabel: actionLabel,
    });

    if (!confirmed) return;

    setLoading(true);
    try {
      await updateQuestionActiveStatus(row, newStatus);
    } catch (error) {
      console.error('Error updating active status:', error);
    } finally {
      setLoading(false);
      invalidateQuestionListCache();
      getUsers(questionPage, debouncedKeyword);
    }
  };

  const handleDelete = async (row) => {
    if (!guardAction('action.assessment.question.delete')) return;
    if (!row) return;
    const confirmed = await askConfirmation({
      title: 'Delete question?',
      message: 'Are you sure you want to delete this question?',
      confirmLabel: 'Delete',
      variant: 'danger',
    });

    if (!confirmed) return;

    setLoading(true);
    try {
      await axios.put(
        `${apiUrl}/api/delete-question`,
        null,
        {
          params: {
            questionId: row.questionId,
            isDeleted: 1,
          },
          headers: {
            'Content-Type': 'application/json',
            Accept: '*/*',
            'Access-Control-Allow-Origin': '*',
            Authorization: getBasicAuthHeader(),
          },
        },
      );
    } catch (error) {
      console.error('Error deleting question:', error);
    } finally {
      setLoading(false);
      invalidateQuestionListCache();
      getUsers(questionPage, debouncedKeyword);
    }
  };

  const closeGenerateDialog = useCallback(() => setGenerateOpen(false), []);
  const assessmentStub = useMemo(
    () => ({ testId, name: fromLabel || 'Assessment' }),
    [testId, fromLabel],
  );

  if (!testId) {
    return (
      <PageShell>
        <DashboardNavbar menuItems={menuItems} />
        <EmptyState>
          <p>Select an assessment from the Assessments page to review its questions.</p>
          <Btn $variant="ghost" onClick={() => navigate('/Portal/Instructor/Assignment')}>
            Go to assessments
          </Btn>
        </EmptyState>
      </PageShell>
    );
  }

  const pageTitle = fromLabel || 'Questions';

  return (
    <PageShell>
      <DashboardNavbar menuItems={menuItems} />
      <BackBar>
        <BackBtn type="button" onClick={() => navigate(-1)}>
          <Icon name="chevL" size={16} />
          Back
        </BackBtn>
      </BackBar>
      <PageHero>
        <div>
          <h2>{pageTitle}</h2>
          <p>{rowCount} questions. Review, edit, enable or disable, and manage answers.</p>
        </div>
        {aiBuilderEnabled && (
        <PageActionsMenu
          primaryLabel="Generate questions"
          primaryIcon={<Sparkles size={16} strokeWidth={2.25} aria-hidden="true" />}
          onPrimaryClick={() => {
            if (!guardAction('action.assessment.question.generate')) return;
            if (!canUseAiCourseBuilder(entitlements)) {
              openUpgradeGate(aiCourseBuilderGateBody(entitlements));
              return;
            }
            setGenerateOpen(true);
          }}
        />
        )}
      </PageHero>
      <TableWrap>
        <DataTable
          rows={sortedTableData}
          loading={loading}
          rowKey={(r, i) => `${r.id ?? r.questionId ?? 'q'}-${i}`}
          pageSize={QUESTION_PAGE_SIZE}
          serverSide
          page={questionPage + 1}
          totalPages={questionTotalPages}
          totalElements={rowCount}
          onPageChange={handleQuestionPageChange}
          sortBy={questionSortBy}
          sortDir={questionSortDir}
          onSortChange={handleQuestionSortChange}
          searchValue={searchKeyword}
          onSearchChange={setSearchKeyword}
          search
          searchPlaceholder="Search questions, answers…"
          primary={{
            header: 'Question',
            sortKey: QUESTION_SORT_FIELDS.question,
            label: (r) => r.question,
            short: (r, i) => String(questionPage * QUESTION_PAGE_SIZE + i + 1),
            minWidth: '36%',
          }}
          columns={[
            {
              header: 'Correct answer',
              sortKey: QUESTION_SORT_FIELDS.correctAnswer,
              render: (r) => r.correctAnswer,
              muted: true,
              wrap: true,
              minWidth: '200px',
              width: '24%',
            },
            {
              header: <LabelWithHelp help={TABLE_HELP.questionCategory}>Question type</LabelWithHelp>,
              sortKey: QUESTION_SORT_FIELDS.questionType,
              render: (r) => QUESTION_TYPE_LABELS[r.questionType] || r.questionType || '—',
              muted: true,
              minWidth: '128px',
              width: '128px',
            },
            {
              header: <LabelWithHelp help={TABLE_HELP.questionStatus}>Status</LabelWithHelp>,
              sortKey: QUESTION_SORT_FIELDS.active,
              minWidth: '112px',
              width: '112px',
              render: (r) => (
                <StatusBadge
                  $active={r.active === 1}
                  style={{ cursor: 'pointer' }}
                  title={r.active === 1 ? 'Disable' : 'Enable'}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleActive(r);
                  }}
                >
                  {r.active === 1 ? 'Enabled' : 'Disabled'}
                </StatusBadge>
              ),
            },
          ]}
          actionsColumnWidth={52}
          actions={(r) => (
            <QuestionRowActionsMenu
              row={r}
              onEdit={handleUpdate}
              onToggleStatus={handleActive}
              onDelete={handleDelete}
            />
          )}
          emptyText="No questions yet."
        />
      </TableWrap>
      <FormDialog
        open={open}
        handleClose={handleClose}
        data={formData}
        onChange={onChange}
        handleFormSubmit={handleFormSubmit}
        loading={loading}
      />
      <GenerateQuestiondialog
        open={generateOpen}
        handleClose={closeGenerateDialog}
        data={assessmentStub}
        testId={testId}
      />
      <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </PageShell>
  );
};

export default QuestionsPage;
